import { useEffect, useReducer, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Editor as ClassicEditor } from "ckeditor5-custom-build/build/ckeditor";
import HelmetTags from "../../../../MainComponents/HelmetTags";

import QuestionCanvas from "./tools/QuestionCanvas";

const AdminQuestionCorrectionForm = () => {
  const [content, setContent] = useState("");
  const { questionId } = useParams();
  const draftFileRef = useRef(null);
  const questionCanvasRef = useRef(null);
  const questionCanvasWrapperRef = useRef(null);

  const [QuestionInfo] = useAxios(
    `${process.env.REACT_APP_ADMIN_QUESTION_CORRECTION_SINGLE_API}/${questionId}`,
    "GET",
    questionId,
    questionId,
  );

  const navigate = useNavigate();

  function reducer(state, action) {
    switch (action.type) {
      case "setErrors": {
        return {
          ...state,
          errors: action.payload,
        };
      }

      case "setSubmitAddEditChapter": {
        return {
          ...state,
          submitAddEditChapter: action.payload,
        };
      }

      default:
        throw Error("Unknown action: " + action.type);
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    submitAddEditChapter: {
      flag: "",
      dependency: false,
      data: {},
    },
  });

  const [
    QuestionCorrectionSuccess,
    QuestionCorrectionErrors,
    QuestionCorrectionLoading,
  ] = useAxios(
    process.env.REACT_APP_ADMIN_QUESTION_CORRECTION_API,
    "POST",
    state.submitAddEditChapter.flag,
    state.submitAddEditChapter.dependency,
    state.submitAddEditChapter.data,
    true,
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({ mode: "onChange" });

  useEffect(() => {
    if (QuestionCorrectionSuccess) {
      navigate(-1);
      reset();
    }
  }, [QuestionCorrectionSuccess, navigate, reset]);

  const onSubmit = async (data) => {
    const finalData = new FormData();
    finalData.append("id", questionId);
    finalData.append("comment", content);

    const selectedDraft =
      Array.isArray(data.draft) ? data.draft[0] : data.draft?.[0] || null;

    let latestDraftFile = selectedDraft || draftFileRef.current;

    if (
      !selectedDraft &&
      QuestionInfo?.data?.draft &&
      questionCanvasRef.current?.exportImage
    ) {
      latestDraftFile =
        (await questionCanvasRef.current.exportImage()) || latestDraftFile;
    }

    if (latestDraftFile) {
      finalData.append(
        "draft",
        latestDraftFile,
        latestDraftFile.name || "question-correction.png",
      );
    }

    finalData.append("degree", data.degree);

    dispatch({
      type: "setSubmitAddEditChapter",
      payload: {
        flag: "AddEditChapter",
        dependency: !state.submitAddEditChapter.dependency,
        data: finalData,
      },
    });
  };

  return (
    <section className="h-full w-full overflow-y-auto px-20 py-20 sm:px-10 ">
      <HelmetTags title="Correction | Mr.Ahmed Sabry"></HelmetTags>
      <form
        encType="multipart/form-data"
        method="post"
        onSubmit={handleSubmit(onSubmit)}
        className="flex   flex-col items-start justify-center gap-10 rounded-3xl bg-white p-16 shadow-3xl"
      >
        <input
          className="signin-inputs w-full pl-4     "
          type="file"
          id="draft"
          placeholder="draft"
          name="draft"
          autoComplete="on"
          step="any"
          hidden
          {...register("draft", {
            required: false,
            onChange: (event) => {
              const file = event.target.files?.[0] || null;
              draftFileRef.current = file;
            },
            validate: (value) => {
              const file = Array.isArray(value)
                ? value[0]
                : value?.[0] || value || null;

              return !(file?.size > 50000000);
            },
          })}
        />
        <div className=" flex w-full flex-col items-end justify-center gap-6 ">
          <div
            className={`flex w-full flex-col items-end justify-center gap-2 md:order-2  md:w-full `}
          >
            <label className="w-full truncate" htmlFor="degree">
              Degree
            </label>
            <input
              className="signin-inputs w-full pl-4    "
              type="number"
              id="degree"
              placeholder="Degree"
              name="degree"
              autoComplete="on"
              step="any"
              {...register("degree", {
                required: true,
                max: QuestionInfo?.data?.degree ?? 40,
                min: 0,
              })}
            />
            {errors.degree && (
              <p className="w-full text-end text-[12px] text-red-500  ">
                {errors.degree.type === "required" && "Please Fill This input"}
                {errors.degree.type === "max" && `Value exceeds maximum allowed ${QuestionInfo?.data?.degree ?? 40}`}
                {errors.degree.type === "min" && "Value is below minimum allowed"}
              </p>
            )}

            {
              //!-------server errors -----

              QuestionCorrectionErrors &&
                QuestionCorrectionErrors?.response?.data?.errors?.degree && (
                  <p className="w-full text-end text-[12px] text-red-500  ">
                    {
                      QuestionCorrectionErrors?.response?.data?.errors
                        ?.degree[0]
                    }
                  </p>
                )
            }
          </div>
          <div className="content mb-10  w-full">
            <h2 className=" mb-3 font-semibold">Comment</h2>
            <CKEditor
              config={{
                toolbar: {
                  items: [
                    "undo",
                    "redo",
                    "|",
                    "imageInsert",
                    "|",
                    "heading",
                    "bold",
                    "italic",
                    "strikethrough",
                    "underline",
                    "link",
                    "blockQuote",
                    "removeFormat",
                    "|",
                    "selectAll",
                    "specialCharacters",
                    "superscript",
                    "subscript",
                    "style",
                    "|",
                    "bulletedList",
                    "numberedList",
                    "outdent",
                    "indent",
                    "|",
                    "mediaEmbed",
                    "insertTable",
                    "|",
                    "fontFamily",
                    "fontColor",
                    "fontBackgroundColor",
                    "fontSize",
                    "highlight",
                    "|",
                    "horizontalLine",
                    "pageBreak",
                    "findAndReplace",
                    "restrictedEditingException",
                    "textPartLanguage",
                    "-",
                  ],
                  shouldNotGroupWhenFull: true,
                },
                language: "en",
                image: {
                  toolbar: [
                    "imageTextAlternative",
                    "toggleImageCaption",
                    "imageStyle:inline",
                    "imageStyle:block",
                    "imageStyle:side",
                  ],
                },
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                    "tableCellProperties",
                    "tableProperties",
                  ],
                },
              }}
              editor={ClassicEditor}
              data={content}
              onChange={(event, editor) => {
                const data = editor.getData();
                setContent(data);
              }}
            />
            {errors === "content" && (
              <p className="w-full text-end text-[12px] text-red-500   ">
                {" "}
                Please Fill This Comment
              </p>
            )}
            {
              //!-------server errors -----

              QuestionCorrectionErrors &&
                QuestionCorrectionErrors?.response?.data?.errors?.content && (
                  <p className="w-full text-end text-[12px] text-red-500  ">
                    {
                      QuestionCorrectionErrors?.response?.data?.errors
                        ?.content[0]
                    }
                  </p>
                )
            }
          </div>

          {QuestionInfo?.data?.draft && (
            <div
              ref={questionCanvasWrapperRef}
              style={{ position: "relative", width: "100%", height: "auto" }}
            >
              <QuestionCanvas
                ref={questionCanvasRef}
                imageSrc={QuestionInfo?.data?.draft}
                onExport={(file) => {
                  draftFileRef.current = file;
                }}
                containerRef={questionCanvasWrapperRef}
              />
            </div>
          )}
        </div>

        {/** submit */}
        <button
          disabled={
            !isValid ||
            (QuestionCorrectionLoading && state.submitAddEditChapter.flag)
          }
          className=" submit mt-6 w-full "
          type="submit"
        >
          {QuestionCorrectionLoading && state.submitAddEditChapter.flag ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Correct Question</p>
          )}
        </button>
      </form>
    </section>
  );
};

export default AdminQuestionCorrectionForm;
